import React, { useEffect, useState, useMemo } from "react";
import InventoryItem from "./InventoryItem/InventoryItem";
import { db } from "../../firebase";
import { collection, getDocs, doc, deleteDoc } from "firebase/firestore";
import { Link } from "react-router-dom";
import "./Inventory.scss";

function Inventory() {
  const [fullInventory, setFullInventory] = useState([]);
  const [deleteItem, setDeleteItem] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [jewelleryFilter, setJewelleryFilter] = useState("");
  const [availableFilter, setAvailableFilter] = useState("");
  const [featuredFilter, setFeaturedFilter] = useState("");

  useEffect(() => {
    const getdata = async () => {
      const productsCollectionRef = collection(db, "products");
      const data = await getDocs(productsCollectionRef);
      let results = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      results.sort((a, b) => (a.title > b.title ? 1 : -1));
      setFullInventory(results);
      document.title = "Branded Jewellery UK";
    };
    getdata();
  }, []);

  const brandOptions = useMemo(() => {
    const set = new Set(fullInventory.map((p) => p.brand).filter(Boolean));
    return [...set].sort();
  }, [fullInventory]);

  const jewelleryOptions = useMemo(() => {
    const set = new Set(fullInventory.map((p) => p.jewellery).filter(Boolean));
    return [...set].sort();
  }, [fullInventory]);

  const filteredInventory = useMemo(() => {
    let results = fullInventory;

    if (searchQuery.trim()) {
      const terms = searchQuery.toLowerCase().trim().split(/\s+/);
      results = results.filter((item) => {
        const searchable = [
          item.title,
          item.sku,
          item.brand,
          item.jewellery,
          item.description,
          item.metal,
          item.gem,
          item.brandRef,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return terms.every((term) => searchable.includes(term));
      });
    }

    if (brandFilter) {
      results = results.filter((item) => item.brand === brandFilter);
    }
    if (jewelleryFilter) {
      results = results.filter((item) => item.jewellery === jewelleryFilter);
    }
    if (availableFilter) {
      results = results.filter((item) => item.available === availableFilter);
    }
    if (featuredFilter) {
      results = results.filter((item) => item.featured === featuredFilter);
    }

    return results;
  }, [
    fullInventory,
    searchQuery,
    brandFilter,
    jewelleryFilter,
    availableFilter,
    featuredFilter,
  ]);

  const hasActiveFilters =
    searchQuery || brandFilter || jewelleryFilter || availableFilter || featuredFilter;

  const clearFilters = () => {
    setSearchQuery("");
    setBrandFilter("");
    setJewelleryFilter("");
    setAvailableFilter("");
    setFeaturedFilter("");
  };

  const handleDelete = async () => {
    const productDoc = doc(db, "products", deleteItem);
    await deleteDoc(productDoc);
    setDeleteItem(null);
    const productsCollectionRef = collection(db, "products");
    const data = await getDocs(productsCollectionRef);
    let results = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
    results.sort((a, b) => (a.title > b.title ? 1 : -1));
    setFullInventory(results);
  };

  const handleCancel = () => {
    setDeleteItem(null);
  };

  return (
    <div className="inventory-page">
      <div className="inventory-page__nav">
        <Link to="/bj-admin">
          <button className="navb">Admin Home</button>
        </Link>
        <Link to="inventory/new-product">
          <button className="navb">Add New Product</button>
        </Link>
      </div>

      <div className="inv-toolbar">
        <div className="inv-toolbar__search">
          <input
            type="text"
            placeholder="Search by title, SKU, brand, description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="inv-toolbar__search-input"
          />
        </div>

        <div className="inv-toolbar__filters">
          <select
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
            className="inv-toolbar__select"
          >
            <option value="">All Brands</option>
            {brandOptions.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>

          <select
            value={jewelleryFilter}
            onChange={(e) => setJewelleryFilter(e.target.value)}
            className="inv-toolbar__select"
          >
            <option value="">All Jewellery Types</option>
            {jewelleryOptions.map((j) => (
              <option key={j} value={j}>
                {j}
              </option>
            ))}
          </select>

          <select
            value={availableFilter}
            onChange={(e) => setAvailableFilter(e.target.value)}
            className="inv-toolbar__select"
          >
            <option value="">All Statuses</option>
            <option value="Is Available">Available</option>
            <option value="Not Available">Not Available</option>
            <option value="Sold">Sold</option>
            <option value="Enquire">Enquire</option>
          </select>

          <select
            value={featuredFilter}
            onChange={(e) => setFeaturedFilter(e.target.value)}
            className="inv-toolbar__select"
          >
            <option value="">All Items</option>
            <option value="true">Featured</option>
            <option value="false">Not Featured</option>
          </select>

          {hasActiveFilters && (
            <button
              type="button"
              className="inv-toolbar__clear"
              onClick={clearFilters}
            >
              Clear Filters
            </button>
          )}
        </div>

        <p className="inv-toolbar__count">
          Showing {filteredInventory.length} of {fullInventory.length} products
        </p>
      </div>

      <div className="header">
        <h3 className="header__title">Title</h3>
        <h3 className="header__img">Image</h3>
        <h3 className="header__sku">SKU</h3>
        <h3 className="header__status">Status</h3>
        <h3 className="header__date">Date Modified</h3>
      </div>
      {filteredInventory.map((item) => {
        return (
          <div key={item.id}>
            <InventoryItem
              data={item}
              key={item.id}
              setDelete={setDeleteItem}
            />
            {deleteItem === item.id && (
              <div className="delete-modal">
                <h3>Are you sure you want to delete this item?</h3>
                <button type="button" onClick={handleDelete}>
                  Delete
                </button>
                <button type="button" onClick={handleCancel}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        );
      })}

      {filteredInventory.length === 0 && fullInventory.length > 0 && (
        <p className="inventory-page__no-results">
          No products match your current filters.
        </p>
      )}
    </div>
  );
}

export default Inventory;
